import { useState, useEffect, useCallback } from 'react';
import { getProjectSupplierCostDocuments } from '../services/supplierCostDocumentService';
import type { SupplierCostDocumentView } from '../types';

interface UseSupplierCostDocumentsOptions {
  projectId: string | null;
  autoFetch?: boolean;
}

interface UseSupplierCostDocumentsReturn {
  documents: SupplierCostDocumentView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSupplierCostDocuments({
  projectId,
  autoFetch = true,
}: UseSupplierCostDocumentsOptions): UseSupplierCostDocumentsReturn {
  const [documents, setDocuments] = useState<SupplierCostDocumentView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!projectId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getProjectSupplierCostDocuments(projectId);
      setDocuments(data);
    } catch (err) {
      console.error('Error fetching supplier cost documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoFetch) {
      fetchDocuments();
    }
  }, [autoFetch, fetchDocuments]);

  return {
    documents,
    isLoading,
    error,
    refetch: fetchDocuments,
  };
}
